import { forwardRef, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { cn, INPUT_TRIGGER_CLS } from "@/lib/utils";

export interface SearchableOption {
  value: string;
  label: string;
  hint?: string;
  keywords?: string;
}

export const SEARCHABLE_MIN_OPTIONS = 8;

const isTouchPointer = () =>
  typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches === true;

export interface SelectPopoverProps {
  value: string;
  onValueChange: (value: string) => void;
  selected: SearchableOption | null;
  items: SearchableOption[];
  showSearch: boolean;
  query: string;
  onQueryChange: (query: string) => void;
  onOpenChange?: (open: boolean) => void;
  loading?: boolean;
  loadingMore?: boolean;
  footer?: ReactNode;
  onScrollEnd?: () => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  id?: string;
  disabled?: boolean;
  icon?: ReactNode;
  className?: string;
  contentClassName?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

export const SelectPopover = forwardRef<HTMLButtonElement, SelectPopoverProps>(
  function SelectPopover({
    value,
    onValueChange,
    selected,
    items,
    showSearch,
    query,
    onQueryChange,
    onOpenChange,
    loading,
    loadingMore,
    footer,
    onScrollEnd,
    placeholder = "Select…",
    searchPlaceholder = "Search…",
    emptyText = "No matches.",
    id,
    disabled,
    icon,
    className,
    contentClassName,
    ...aria
  }, ref) {
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    function changeOpen(next: boolean) {
      setOpen(next);
      onOpenChange?.(next);
    }

    useEffect(() => {
      if (!open) {
        onQueryChange("");
        return;
      }
      const index = items.findIndex((o) => o.value === value);
      setActive(index < 0 ? 0 : index);
    }, [open]);

    useEffect(() => {
      setActive(0);
    }, [query]);

    useEffect(() => {
      const list = listRef.current;
      const item = list?.querySelector<HTMLElement>('[data-active="true"]');
      if (!list || !item) return;
      const listBox = list.getBoundingClientRect();
      const itemBox = item.getBoundingClientRect();
      if (itemBox.top < listBox.top) list.scrollTop -= listBox.top - itemBox.top;
      else if (itemBox.bottom > listBox.bottom) list.scrollTop += itemBox.bottom - listBox.bottom;
    }, [active, open]);

    function pick(next: string) {
      onValueChange(next);
      changeOpen(false);
    }

    function onKeyDown(e: React.KeyboardEvent) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        if (items.length === 0) return;
        const step = e.key === "ArrowDown" ? 1 : -1;
        setActive((i) => (i + step + items.length) % items.length);
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const hit = items[active];
        if (hit) pick(hit.value);
      }
    }

    function onScroll() {
      const list = listRef.current;
      if (!list || !onScrollEnd) return;
      if (list.scrollTop + list.clientHeight >= list.scrollHeight - 32) onScrollEnd();
    }

    return (
      <Popover open={open} onOpenChange={changeOpen}>
        <PopoverTrigger
          {...aria}
          ref={ref}
          id={id}
          role="combobox"
          aria-haspopup="listbox"
          disabled={disabled}
          className={cn(
            INPUT_TRIGGER_CLS,
            !selected && "text-muted-foreground",
            className,
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            {icon}
            <span className="truncate text-left">{selected?.label ?? placeholder}</span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={cn("w-[--radix-popover-trigger-width] p-0", contentClassName)}
          onKeyDown={onKeyDown}
          onOpenAutoFocus={(e) => {
            if (isTouchPointer()) {
              e.preventDefault();
              return;
            }
            if (showSearch) {
              e.preventDefault();
              searchRef.current?.focus({ preventScroll: true });
            }
          }}
        >
          {showSearch && (
            <div className="flex items-center gap-2 border-b px-3">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                placeholder={searchPlaceholder}
                spellCheck={false}
                className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
              {loading && <Spinner className="shrink-0 text-muted-foreground" />}
            </div>
          )}
          <div
            ref={listRef}
            role="listbox"
            onScroll={onScroll}
            className="max-h-64 overflow-y-auto p-1"
          >
            {items.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                {loading ? "Loading…" : emptyText}
              </p>
            ) : (
              <>
                {items.map((o, i) => (
                  <button
                    key={o.value}
                    type="button"
                    role="option"
                    aria-selected={o.value === value}
                    data-active={i === active}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => pick(o.value)}
                    className={cn(
                      "flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left text-sm",
                      i === active && "bg-accent text-accent-foreground",
                    )}
                  >
                    <Check
                      className={cn(
                        "mt-0.5 h-4 w-4 shrink-0",
                        o.value === value ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{o.label}</span>
                      {o.hint && (
                        <span className="block truncate text-xs text-muted-foreground">{o.hint}</span>
                      )}
                    </span>
                  </button>
                ))}
                {loadingMore && (
                  <p className="flex items-center justify-center gap-2 px-2 py-2 text-sm text-muted-foreground">
                    <Spinner /> Loading more…
                  </p>
                )}
              </>
            )}
          </div>
          {footer && (
            <div className="border-t px-3 py-1.5 text-xs text-muted-foreground">{footer}</div>
          )}
        </PopoverContent>
      </Popover>
    );
  },
);

export const SearchableSelect = forwardRef<HTMLButtonElement, {
  value: string;
  onValueChange: (value: string) => void;
  options: SearchableOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  id?: string;
  disabled?: boolean;
  icon?: ReactNode;
  className?: string;
  contentClassName?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}>(function SearchableSelect({ value, onValueChange, options, ...rest }, ref) {
  const [query, setQuery] = useState("");

  const selected = options.find((o) => o.value === value) ?? null;
  const showSearch = options.length >= SEARCHABLE_MIN_OPTIONS;

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      `${o.label} ${o.hint ?? ""} ${o.keywords ?? ""}`.toLowerCase().includes(q),
    );
  }, [options, query]);

  return (
    <SelectPopover
      {...rest}
      ref={ref}
      value={value}
      onValueChange={onValueChange}
      selected={selected}
      items={matches}
      showSearch={showSearch}
      query={query}
      onQueryChange={setQuery}
    />
  );
});
